import { BaseRHFWidgetProps } from "@impero-com/common/components/form/rhfFields/types";
import { FieldValues, UseFormTrigger, useController } from "react-hook-form";

import { ControlProgramTagsQuerySelect } from "org/tags/controlProgramTags/queries/useControlProgramTagsQuery";
import { Guid } from "utils/guid";

import {
    EditableControlProgramTagListInputBaseProps,
    EditableControlProgramTagListPlusInput,
} from "./editableControlProgramTagListPlus";

interface RHFEditableControlProgramTagListInputProps<
    TFieldsValues extends FieldValues,
>
    extends
        BaseRHFWidgetProps<TFieldsValues, Guid[]>,
        EditableControlProgramTagListInputBaseProps {
    initialTags?: ControlProgramTagsQuerySelect;
    // Needs to be supplied if there is validation on array level
    trigger?: UseFormTrigger<TFieldsValues>;
    // Side-effect callback
    onChange?: () => void;
}

export function RHFEditableControlProgramTagListInput<
    TFieldsValues extends FieldValues,
>({
    name,
    control,
    disabled,
    label,
    canCreateTag,
    cascaderClassName,
    initialTags,
    trigger,
    onChange,
}: RHFEditableControlProgramTagListInputProps<TFieldsValues>) {
    const { field, fieldState } = useController({ name, control, disabled });

    const handleBlur = () => {
        field.onBlur();
        trigger?.(name); // RHF expects to use useFieldArray for arrays, with errors at item level
    };

    const handleSelect = (tagIds: Guid[]) => {
        field.onChange(tagIds);
        onChange?.();
    };

    return (
        <EditableControlProgramTagListPlusInput
            inputRef={field.ref}
            onBlur={handleBlur}
            setSelectedTagIds={handleSelect}
            cascaderClassName={cascaderClassName}
            canCreateTag={canCreateTag}
            disabled={field.disabled}
            error={fieldState.error?.message}
            label={label}
            initialTags={initialTags}
            selectedTagIds={field.value}
        />
    );
}
